import { LogInIcon, LogOutIcon, PanelLeftIcon, PlusIcon, SettingsIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/atoms/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/molecules/tooltip";
import { useAccessGuard } from "@/hooks/use-access-guard";
import { useAuth } from "@/hooks/use-auth";
import { signOut } from "@/lib/auth-client";
import { isMacPlatform, newChatShortcutKeys, newChatShortcutLabel } from "@/lib/shortcuts";
import { cn } from "@/lib/utils";
import {
  ThreadListItems,
  ThreadListNew,
  ThreadListRoot,
} from "@/routes/dashboard/components/assistant/thread-list";
import { Wordmark } from "@/shared/wordmark";
import { WorkspaceSwitcher } from "@/shared/workspace-switcher";

type AppSidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  onOpenSettings: () => void;
};

function RailButton({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: ReactNode;
}) {
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

const iconButton =
  "size-8 shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors";

/**
 * The console sidebar: wordmark, "new task", chats, and the workspace/account
 * footer. Collapses to a 56px icon rail.
 */
export function AppSidebar({ collapsed, onToggle, onOpenSettings }: AppSidebarProps) {
  const { user, isAuthenticated } = useAuth();
  const { isAllowed } = useAccessGuard();
  const isMac = isMacPlatform();
  const shortcut = newChatShortcutLabel(isMac);
  const shortcutKeys = newChatShortcutKeys(isMac);

  return (
    <aside
      className={cn(
        "bg-sidebar text-sidebar-foreground border-sidebar-border hidden h-full shrink-0 flex-col border-r transition-[width] duration-200 md:flex",
        collapsed ? "w-14" : "w-62",
      )}
    >
      <div className={cn("flex h-12 shrink-0 items-center gap-2", collapsed ? "px-3" : "px-4")}>
        {!collapsed && <Wordmark />}
        <button
          type="button"
          onClick={onToggle}
          className={cn(iconButton, !collapsed && "ml-auto")}
          aria-label={collapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <PanelLeftIcon className="size-4" />
        </button>
      </div>

      <ThreadListRoot className={cn("flex-1 overflow-y-auto pb-2", collapsed ? "px-3" : "px-2")}>
        <RailButton label={`New task (${shortcut})`} collapsed={collapsed}>
          <ThreadListNew
            className={cn(
              "bg-primary text-primary-foreground data-active:bg-primary data-active:text-primary-foreground hover:bg-brand hover:text-brand-foreground mb-3 transition-colors",
              collapsed ? "size-8 justify-center px-0" : "w-full px-2.5",
            )}
          >
            <PlusIcon className="size-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-start">New task</span>
                <kbd className="text-primary-foreground/60 text-xs">
                  {shortcutKeys[0]?.join(isMac ? "" : "+")}
                </kbd>
              </>
            )}
          </ThreadListNew>
        </RailButton>
        {!collapsed && isAuthenticated && (
          <>
            <div className="border-foreground text-muted-foreground mx-2.5 border-t pt-2 text-xs">
              Chats
            </div>
            <ThreadListItems />
          </>
        )}
      </ThreadListRoot>

      <div
        className={cn(
          "border-sidebar-border flex shrink-0 flex-col gap-1 border-t py-2",
          collapsed ? "items-center px-3" : "px-2",
        )}
      >
        {isAuthenticated ? (
          <>
            {!collapsed && <WorkspaceSwitcher placement="sidebar" />}
            <div className={cn("flex items-center gap-2", collapsed ? "flex-col" : "px-1.5")}>
              <Avatar size="sm">
                <AvatarImage src={user?.image ?? undefined} alt={user?.name ?? user?.email ?? ""} />
                <AvatarFallback>
                  {(user?.name || user?.email || "").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13px]">{user?.name || user?.email}</span>
                  {!isAllowed && <span className="text-warning text-[11px]">Unverified</span>}
                </span>
              )}
              <RailButton label="Settings" collapsed={collapsed}>
                <button
                  type="button"
                  onClick={onOpenSettings}
                  className={iconButton}
                  aria-label="Settings"
                >
                  <SettingsIcon className="size-4" />
                </button>
              </RailButton>
              <RailButton label="Sign out" collapsed={collapsed}>
                <button
                  type="button"
                  onClick={() => {
                    signOut();
                    window.location.reload();
                  }}
                  className={iconButton}
                  aria-label="Sign out"
                >
                  <LogOutIcon className="size-4" />
                </button>
              </RailButton>
            </div>
          </>
        ) : (
          <RailButton label="Sign in" collapsed={collapsed}>
            <Link
              to="/auth/signin"
              aria-label="Sign in"
              className={cn(
                "bg-primary text-primary-foreground hover:bg-primary/90 flex h-8 items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors",
                collapsed ? "size-8" : "w-full",
              )}
            >
              <LogInIcon className="size-4" />
              {!collapsed && "Sign in"}
            </Link>
          </RailButton>
        )}
      </div>
    </aside>
  );
}
